import { Injectable, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CONSTANTS } from '../../Constants/Constants';
import { CommonDataService } from '../../services/common-data.service';

@Injectable({
  providedIn: 'root'
})
@Pipe({
  name: 'multilingualCurrency',
  pure: false,
  standalone: true
})
export class MultilingualCurrencyPipe implements PipeTransform {
  

  constructor(private translateService: TranslateService,
    private readonly commonDataService: CommonDataService
  ) {}

  transform(value: number | string): string {
    const decimalPrecision = this.commonDataService.getDecimalPrecision();
    if (value === null || value === undefined || value === CONSTANTS.EMPTY_STRING) {
      return CONSTANTS.EMPTY_STRING;
    }

    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numericValue)) {
      return value.toString();
    }

    const currentLang = this.translateService.currentLang || this.translateService.defaultLang || CONSTANTS.LANGUAGE_EN_US;
    
    try {
      // Custom formatting logic based on language
      if (currentLang === CONSTANTS.LANGUAGE_FR_CA) {
        // French Canadian: symbol at end, comma for decimal, space for thousands
        // Example: 1 234 567,89 $ CA
        return this.formatFrenchCanadian(numericValue, decimalPrecision);
      } else {
        // English (US & CA): dollar symbol at front, comma for thousands, dot for decimal
        // Example: $1,234,567.89
        return this.formatEnglish(numericValue, decimalPrecision);
      }
    } catch (error) {
      return `${CONSTANTS.DOLLAR_SIGN}${numericValue.toFixed(decimalPrecision)}`;
    }
  }

  private formatEnglish(value: number, decimalPrecision: number): string {
    // Format: $1,234,567.89
    return new Intl.NumberFormat(CONSTANTS.LANGUAGE_EN_US, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimalPrecision,
      maximumFractionDigits: decimalPrecision
    }).format(value);
  }

  private formatFrenchCanadian(value: number, decimalPrecision: number): string {
    // Custom formatting for French Canadian: 1 234 567,89 $ CA
    const absoluteValue = Math.abs(value);
    const isNegative = value < CONSTANTS.ZERO;
    
    // Split into integer and decimal parts
    const fixedValue = absoluteValue.toFixed(decimalPrecision);
    const [integerPart, decimalPart] = fixedValue.split(CONSTANTS.DOT);
    
    // Add spaces every 3 digits from right to left
    const formattedInteger = this.addSpaceSeparators(integerPart);
    
    const formatted = `${formattedInteger},${decimalPart} ${CONSTANTS.DOLLAR_SIGN}`;
    
    return isNegative ? `-${formatted}` : formatted;
  }

  private addSpaceSeparators(numberStr: string): string {
    return numberStr.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }
}
